import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { GameState, Connection, WordPosition } from '../types/game';
import { useWordAnimations } from './useWordAnimations';
import { getRecommendedSettings } from '../utils/browserCompat';
import { useAuth } from './useAuth';
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  saveToFirestore,
  loadFromFirestore,
  savePerformanceToFirestore,
  loadUserPerformanceHistory,
  saveUserSettings,
  loadUserSettings,
  mergeGameStates,
  getTodayKey,
} from '../services/gameStateService';

const emojiCircles = ['🟣', '🔵', '🟢', '🟡', '🔴'];

// Function to format share message by grouping emojis into groups of 5
function formatShareMessage(header: string, emojiString: string): string {
  // Convert emoji string to array of individual emojis (handles Unicode properly)
  const emojis = [...emojiString];
  
  // Split emojis into groups of 5
  const groups = [];
  for (let i = 0; i < emojis.length; i += 5) {
    groups.push(emojis.slice(i, i + 5).join(''));
  }
  return header + '\n' + groups.join('\n');
}

// Helper function to find connected segments in the solution order
function findConnectedSegments(correctConnections: Connection[], wordsInOrder: string[]): string[][] {
  if (correctConnections.length === 0) return [];
  
  const segments: string[][] = [];
  const visited = new Set<string>();
  
  // Build adjacency map from correct connections
  const adjacencyMap = new Map<string, Set<string>>();
  correctConnections.forEach(conn => {
    if (!adjacencyMap.has(conn.from)) adjacencyMap.set(conn.from, new Set());
    if (!adjacencyMap.has(conn.to)) adjacencyMap.set(conn.to, new Set());
    adjacencyMap.get(conn.from)!.add(conn.to);
    adjacencyMap.get(conn.to)!.add(conn.from);
  });
  
  // Find continuous segments by following solution order
  for (const word of wordsInOrder) {
    if (visited.has(word) || !adjacencyMap.has(word)) continue;
    
    const segment: string[] = [];
    let current = word;
    let searchSteps = 0;
    const maxSteps = wordsInOrder.length; // Prevent infinite loops
    
    // Find the start of this segment by going backwards in solution order (with loop protection)
    while (current && adjacencyMap.has(current) && searchSteps < maxSteps) {
      const prevIndex = (wordsInOrder.indexOf(current) - 1 + wordsInOrder.length) % wordsInOrder.length;
      const prevWord = wordsInOrder[prevIndex];
      if (adjacencyMap.get(current)?.has(prevWord) && !visited.has(prevWord)) {
        current = prevWord;
        searchSteps++;
      } else {
        break;
      }
    }
    
    // Build segment going forward in solution order (with loop protection)
    searchSteps = 0;
    while (current && adjacencyMap.has(current) && !visited.has(current) && searchSteps < maxSteps) {
      segment.push(current);
      visited.add(current);
      
      const nextIndex = (wordsInOrder.indexOf(current) + 1) % wordsInOrder.length;
      const nextWord = wordsInOrder[nextIndex];
      if (adjacencyMap.get(current)?.has(nextWord)) {
        current = nextWord;
        searchSteps++;
      } else {
        break;
      }
    }
    
    if (segment.length > 0) {
      segments.push(segment);
    }
  }
  
  return segments;
}

// Main function to arrange connected words circumferentially while keeping unconnected words shuffled
function arrangeConnectedWordsCircumferentially(
  currentWords: string[],
  newWord1: string,
  newWord2: string,
  existingCorrectConnections: Connection[],
  wordsInOrder: string[]
): string[] {
  console.log(`🔄 Arranging words: ${newWord1} + ${newWord2}, existing connections: ${existingCorrectConnections.length}`);
  
  // Add the new connection to the existing ones
  const allCorrectConnections = [...existingCorrectConnections, {
    from: newWord1,
    to: newWord2,
    color: 'green',
    isCorrect: true
  }];
  
  // Find all connected segments
  const connectedSegments = findConnectedSegments(allCorrectConnections, wordsInOrder);
  
  // Get all connected words (words that are part of any segment)
  const connectedWords = new Set<string>();
  connectedSegments.forEach(segment => segment.forEach(word => connectedWords.add(word)));
  
  // Separate connected and unconnected words, preserving positions of unconnected words
  const result = [...currentWords];
  const unconnectedWords = result.filter(word => !connectedWords.has(word));
  
  // Remove all connected words from their current positions
  connectedWords.forEach(word => {
    const index = result.indexOf(word);
    if (index !== -1) result.splice(index, 1);
  });
  
  // Sort segments by their first word's position in solution order for consistent placement
  const sortedSegments = connectedSegments.sort((a, b) => {
    const aIndex = wordsInOrder.indexOf(a[0]);
    const bIndex = wordsInOrder.indexOf(b[0]);
    return aIndex - bIndex;
  });
  
  // Place connected segments back, trying to minimize disruption
  let insertionIndex = 0;
  
  for (const segment of sortedSegments) {
    // Find the best insertion point for this segment
    insertionIndex = Math.min(insertionIndex, result.length);
    
    // Insert the entire segment at once to maintain circumferential order
    result.splice(insertionIndex, 0, ...segment);
    insertionIndex += segment.length;
  }
  
  // Check if the result is actually different from the input to avoid unnecessary re-renders
  const hasChanged = result.length !== currentWords.length || 
    result.some((word, index) => word !== currentWords[index]);
  
  if (!hasChanged) {
    console.log(`🔄 No word arrangement change needed`);
    return currentWords; // Return the same array reference to prevent re-renders
  }
  
  console.log(`🔄 Word arrangement changed: ${currentWords.join(',')} -> ${result.join(',')}`);
  return result;
}

// Helper function to find optimal insertion point for maintaining circumferential topology
function findOptimalInsertionPoint(
  word1: string, 
  word2: string, 
  connectedSegments: string[][], 
  currentWords: string[], 
  wordsInOrder: string[]
): number {
  // Find if either word should extend an existing segment
  for (const segment of connectedSegments) {
    const segmentStart = segment[0];
    const segmentEnd = segment[segment.length - 1];
    
    // Check if the new connection extends this segment
    const word1SolutionIndex = wordsInOrder.indexOf(word1);
    const word2SolutionIndex = wordsInOrder.indexOf(word2);
    const startIndex = wordsInOrder.indexOf(segmentStart);
    const endIndex = wordsInOrder.indexOf(segmentEnd);
    
    // Check if words extend the segment at either end
    const extendsAtStart = 
      (word1SolutionIndex === (startIndex - 1 + wordsInOrder.length) % wordsInOrder.length && word2 === segmentStart) ||
      (word2SolutionIndex === (startIndex - 1 + wordsInOrder.length) % wordsInOrder.length && word1 === segmentStart);
      
    const extendsAtEnd = 
      (word1SolutionIndex === (endIndex + 1) % wordsInOrder.length && word2 === segmentEnd) ||
      (word2SolutionIndex === (endIndex + 1) % wordsInOrder.length && word1 === segmentEnd);
    
    if (extendsAtStart) {
      // Insert before the existing segment
      const segmentStartInWords = currentWords.indexOf(segmentStart);
      return segmentStartInWords;
    }
    
    if (extendsAtEnd) {
      // Insert after the existing segment
      const segmentEndInWords = currentWords.indexOf(segmentEnd);
      return segmentEndInWords + 1;
    }
  }
  
  // If no extension possible, place at beginning to start a new segment
  return 0;
}

const initialGameState: GameState = {
  wordsInOrder: [],
  words: [],
  wordPositions: {},
  connections: [],
  correctPairs: {},
  correctConnectionsCount: {},
  errors: 0,
  maxErrors: 4,
  selectedWord: null,
  secondSelectedWord: null,
  isDragging: false,
  hoveredWord: null,
  gameCompleted: false,
  shareMessage: 'Pathways (pathways1.web.app)\n',
  fontSizeScale: 1.0,
  confirmMode: true,
  dragMode: true,
  lastConnection: null,
  toast: { message: '', show: false, type: 'error' },
  pendingConnection: null,
  showingSolution: false,
  solutionAnimationStep: 0,
};

export const useGameState = () => {
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const { user } = useAuth();
  const { getAnimation, jumpAnimation, shakeAnimation } = useWordAnimations();
  const isGameInitialized = useRef(false);
  const isLoadingState = useRef(false);

  const saveToFirestoreOnMove = useCallback(async (state: GameState) => {
    if (user) {
      const success = await saveToFirestore(state, user);
      if (!success) {
        console.warn("Failed to save to Firestore, but data is saved locally");
      }
    }
  }, [user]);

  useEffect(() => {
    if (isGameInitialized.current && !isLoadingState.current) {
      saveToLocalStorage(gameState);
      // Firestore saves are now handled explicitly only when moves are made
    }
  }, [gameState]);

  // REMOVED: Real-time sync handlers no longer needed

  // Track the current user to detect user changes
  const currentUserRef = useRef<string | null>(null);
  
  // Handle user changes with anti-cheat protection
  useEffect(() => {
    const handleUserChange = async () => {
      const newUserId = user?.uid || null;
      const previousUserId = currentUserRef.current;
      
      if (newUserId !== previousUserId && isGameInitialized.current) {
        console.log('👤 User changed:', { 
          from: previousUserId ? 'logged-in' : 'anonymous', 
          to: newUserId ? user?.email : 'anonymous' 
        });
        
        // Anti-cheat: Don't allow resetting game by switching users on same day
        // Only load saved state if it's better progress than current state
        if (newUserId && !isLoadingState.current) {
          // This will be handled by the merge logic in loadGameState
          // which prioritizes better progress
          console.log('🔍 Checking for saved state without allowing cheating');
        } else if (!newUserId) {
          console.log('👋 User logged out - keeping current game state');
        }
      }
      
      currentUserRef.current = newUserId;
    };

    handleUserChange();
  }, [user]);

  // Load user settings whenever user becomes available (including after hard refresh)
  useEffect(() => {
    const loadSettingsIfAvailable = async () => {
      if (user && isGameInitialized.current && !isLoadingState.current) {
        console.log('🔧 Loading user settings after auth state change');
        try {
          const userSettings = await loadUserSettings(user);
          if (userSettings) {
            console.log('🔧 Applying user settings from Firestore:', userSettings);
            setGameState(prev => ({
              ...prev,
              fontSizeScale: userSettings.fontSizeScale,
              confirmMode: userSettings.confirmMode,
              dragMode: userSettings.dragMode,
            }));
          } else {
            // No settings found - save current settings (likely defaults) to establish user preferences
            console.log('🔧 No user settings found, saving current settings to Firestore');
            setGameState(prev => {
              saveUserSettings(user, {
                fontSizeScale: prev.fontSizeScale,
                confirmMode: prev.confirmMode,
                dragMode: prev.dragMode,
              });
              return prev;
            });
          }
        } catch (error) {
          console.error('Error loading user settings:', error);
        }
      }
    };

    loadSettingsIfAvailable();
  }, [user]);

  // REMOVED: Real-time sync setup no longer needed

  const shuffleArray = (array: string[]) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const generateCorrectPairs = (wordsInOrder: string[]) => {
    const pairs: Record<string, string[]> = {};
    for (let i = 0; i < wordsInOrder.length; i++) {
      const word = wordsInOrder[i];
      const prevWord = wordsInOrder[(i - 1 + wordsInOrder.length) % wordsInOrder.length];
      const nextWord = wordsInOrder[(i + 1) % wordsInOrder.length];
      pairs[word] = [prevWord, nextWord];
    }
    return pairs;
  };

  const initializeGame = useCallback((wordList: string[]) => {
    if (isGameInitialized.current) return;
    isGameInitialized.current = true;

    const correctPairs = generateCorrectPairs(wordList);
    const correctConnectionsCount = wordList.reduce((acc, word) => {
      acc[word] = 0;
      return acc;
    }, {} as Record<string, number>);

    const recommendedSettings = getRecommendedSettings();

    setGameState(prev => ({
      ...initialGameState,
      wordsInOrder: wordList,
      words: shuffleArray(wordList),
      correctPairs,
      correctConnectionsCount,
      dragMode: recommendedSettings.dragMode,
      confirmMode: recommendedSettings.confirmMode,
      fontSizeScale: prev.fontSizeScale,
    }));
  }, []);

  const loadGameState = useCallback(async () => {
    if (isLoadingState.current) return false;
    isLoadingState.current = true;

    try {
      const localState = loadFromLocalStorage();
      const cloudState = user ? await loadFromFirestore(user) : null;
      const userSettings = user ? await loadUserSettings(user) : null;
      const mergedState = mergeGameStates(localState, cloudState);

      if (mergedState) {
        console.log("Loading merged game state:", {
          hasLocal: !!localState,
          hasCloud: !!cloudState,
          hasSettings: !!userSettings,
          connections: mergedState.connections.length,
          errors: mergedState.errors
        });
        
        setGameState(prev => ({
          ...prev,
          ...mergedState,
          wordsInOrder: prev.wordsInOrder.length ? prev.wordsInOrder : mergedState.wordsInOrder || [],
          correctPairs: prev.wordsInOrder.length ? prev.correctPairs : generateCorrectPairs(mergedState.wordsInOrder || []),
          // Apply user settings from Firestore if available
          fontSizeScale: userSettings?.fontSizeScale ?? mergedState.fontSizeScale ?? prev.fontSizeScale,
          confirmMode: userSettings?.confirmMode ?? mergedState.confirmMode ?? prev.confirmMode,
          dragMode: userSettings?.dragMode ?? mergedState.dragMode ?? prev.dragMode,
        }));
        return true;
      } else if (userSettings) {
        // Even if no game state, apply user settings
        console.log("Applying user settings from Firestore");
        setGameState(prev => ({
          ...prev,
          fontSizeScale: userSettings.fontSizeScale,
          confirmMode: userSettings.confirmMode,
          dragMode: userSettings.dragMode,
        }));
      }
    } catch (error) {
      console.error("Failed to load game state:", error);
    } finally {
      isLoadingState.current = false;
    }
    return false;
  }, [user]);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'error') => {
    setGameState(prev => ({ ...prev, toast: { message, show: true, type } }));
    setTimeout(() => hideToast(), 2000);
  }, []);
  const hideToast = useCallback(() => setGameState(prev => ({ ...prev, toast: { ...prev.toast, show: false } })), []);

  const actualValidateConnection = useCallback((word1: string, word2: string) => {
    if (gameState.gameCompleted) {
      console.log('🚫 Connection rejected: game already completed');
      return;
    }

    const existingConnection = gameState.connections.find(conn => 
      (conn.from === word1 && conn.to === word2) ||
      (conn.from === word2 && conn.to === word1)
    );

    if (existingConnection) {
      console.log('🚫 Connection rejected: already exists');
      showToast('Already guessed...');
      return;
    }

    console.log(`🎯 Processing connection: ${word1} <-> ${word2}`);

    setGameState(prev => ({ ...prev, secondSelectedWord: word2 }));

    const isValid = 
      (gameState.correctPairs[word1] && gameState.correctPairs[word1].includes(word2)) ||
      (gameState.correctPairs[word2] && gameState.correctPairs[word2].includes(word1));

    setTimeout(() => {
      if (isValid) {
        jumpAnimation(word1, word2);
        if (gameState.jumpWords) gameState.jumpWords(word1, word2);
      } else {
        shakeAnimation(word1, word2);
        if (gameState.wiggleWords) gameState.wiggleWords(word1, word2);
      }

      setTimeout(() => {
        setGameState(prev => {
          const newConnection: Connection = { from: word1, to: word2, color: isValid ? 'green' : 'transparent', isCorrect: isValid };
          let newErrors = prev.errors;
          let newCorrectConnectionsCount = { ...prev.correctConnectionsCount };
          let newShareMessage = prev.shareMessage;

          if (!isValid) {
            newErrors += 1;
            showToast('Incorrect...');
          } else {
            newCorrectConnectionsCount[word1] = (newCorrectConnectionsCount[word1] || 0) + 1;
            newCorrectConnectionsCount[word2] = (newCorrectConnectionsCount[word2] || 0) + 1;
            
            // Add emoji for this correct connection
            newShareMessage += emojiCircles[prev.errors];
          }

          // If it's a correct connection, arrange words to maintain circumferential solution order
          let newWords = [...prev.words];
          if (isValid) {
            const word1SolutionIndex = prev.wordsInOrder.indexOf(word1);
            const word2SolutionIndex = prev.wordsInOrder.indexOf(word2);
            
            // Check if they should be adjacent in the solution
            const shouldBeAdjacent = 
              Math.abs(word1SolutionIndex - word2SolutionIndex) === 1 ||
              (word1SolutionIndex === 0 && word2SolutionIndex === prev.wordsInOrder.length - 1) ||
              (word2SolutionIndex === 0 && word1SolutionIndex === prev.wordsInOrder.length - 1);
            
            if (shouldBeAdjacent) {
              // Check if this will be the final connection - if so, skip rearrangement to allow end game
              const currentCorrectConnections = prev.connections.filter(c => c.isCorrect).length;
              const totalNeededConnections = prev.wordsInOrder.length;
              const willCompleteGame = (currentCorrectConnections + 1) >= totalNeededConnections;
              
              if (willCompleteGame) {
                console.log(`🏁 Final connection detected, skipping word rearrangement to trigger end game`);
                // Don't rearrange on final connection - let the game completion logic trigger the final animation
              } else {
                newWords = arrangeConnectedWordsCircumferentially(
                  newWords, 
                  word1, 
                  word2, 
                  prev.connections.filter(c => c.isCorrect), 
                  prev.wordsInOrder
                );
              }
            }
          }

          let newState = {
            ...prev,
            words: newWords,
            connections: [...prev.connections, newConnection],
            errors: newErrors,
            correctConnectionsCount: newCorrectConnectionsCount,
            selectedWord: null,
            secondSelectedWord: null,
            isDragging: false,
            hoveredWord: null,
            lastConnection: newConnection,
            shareMessage: newShareMessage,
          };

          const totalValidConnections = newState.wordsInOrder.length;
          const greenConnections = newState.connections.filter(conn => conn.isCorrect).length;

          console.log(`🎯 Game completion check: ${greenConnections}/${totalValidConnections} connections, errors: ${newState.errors}/${newState.maxErrors}`);

          if (totalValidConnections > 0 && (greenConnections >= totalValidConnections || newState.errors >= newState.maxErrors)) {
            console.log(`🏁 Game completion triggered! Completed: ${greenConnections >= totalValidConnections}, Max errors: ${newState.errors >= newState.maxErrors}`);
            const completionMessages = { 0: "1st Circle: Perfect!", 1: "2nd Circle: Great!", 2: "3rd Circle: Good job!", 3: "4th Circle: Phew!", 4: "5th Circle: Next time!" };
            if (newState.errors >= newState.maxErrors) {
              // Game over - max errors reached
              newState.gameCompleted = true;
              const emojiString = newState.shareMessage.split('\n').slice(1).join('');
              newState.shareMessage = formatShareMessage('Pathways (pathways1.web.app)\n5th Circle: Next time!', emojiString);
              showToast("Next time!", 'error');
              setTimeout(() => startSolutionAnimationRef.current(navigationCallbackRef.current || undefined), 2000);
              
              // Save performance data for failed attempt
              if (user) {
                savePerformanceToFirestore(user, { 
                  errors: newState.errors, 
                  completedAt: new Date().toISOString() 
                }).then(success => {
                  if (success) {
                    console.log("Game over performance data saved successfully");
                  } else {
                    console.warn("Failed to save game over performance data to cloud");
                  }
                }).catch(error => {
                  console.error("Error saving game over performance data:", error);
                });
              }
            } else {
              // Successful completion!
              const message = completionMessages[newState.errors as keyof typeof completionMessages] || "Game Over!";
              newState.gameCompleted = true;
              const emojiString = newState.shareMessage.split('\n').slice(1).join('');
              newState.shareMessage = formatShareMessage('Pathways (pathways1.web.app)\n' + message, emojiString);
              
              // Show solution animation then navigate to completion modal
              setTimeout(() => startSolutionAnimationRef.current(navigationCallbackRef.current || undefined), 1000);
              
              if (user) {
                savePerformanceToFirestore(user, { 
                  errors: newState.errors, 
                  completedAt: new Date().toISOString() 
                }).then(success => {
                  if (success) {
                    console.log("Performance data saved successfully");
                  } else {
                    console.warn("Failed to save performance data to cloud");
                  }
                }).catch(error => {
                  console.error("Error saving performance data:", error);
                });
              }
            }
          }
          
          // Save to Firestore only when a move is made (connection attempt)
          saveToFirestoreOnMove(newState);
          
          return newState;
        });
      }, 800);
    }, 500);
  }, [gameState.correctPairs, gameState.gameCompleted, gameState.connections, jumpAnimation, shakeAnimation, showToast, user, saveToFirestoreOnMove]);

  const selectWord = useCallback((word: string | null) => setGameState(prev => ({ ...prev, selectedWord: word, secondSelectedWord: null })), []);
  const setDragging = useCallback((dragging: boolean) => setGameState(prev => ({ ...prev, isDragging: dragging, hoveredWord: dragging ? prev.hoveredWord : null })), []);
  const setHoveredWord = useCallback((word: string | null) => setGameState(prev => ({ ...prev, hoveredWord: word })), []);
  const setDragMode = useCallback((dragMode: boolean) => {
    setGameState(prev => ({ ...prev, dragMode, selectedWord: null, secondSelectedWord: null, isDragging: false, hoveredWord: null }));
    // Always save to Firestore when user is available
    if (user) {
      saveUserSettings(user, { dragMode });
    }
  }, [user]);
  const showConfirmationDialog = useCallback((word1: string, word2: string) => setGameState(prev => ({ ...prev, pendingConnection: { word1, word2 } })), []);
  const hideConfirmationDialog = useCallback(() => setGameState(prev => ({ ...prev, pendingConnection: null })), []);

  const confirmConnection = useCallback(() => {
    if (gameState.pendingConnection) {
      const { word1, word2 } = gameState.pendingConnection;
      hideConfirmationDialog();
      setTimeout(() => actualValidateConnection(word1, word2), 100);
    }
  }, [gameState.pendingConnection, actualValidateConnection, hideConfirmationDialog]);

  const validateConnection = useCallback((word1: string, word2: string) => {
    if (gameState.confirmMode) {
      showConfirmationDialog(word1, word2);
    } else {
      actualValidateConnection(word1, word2);
    }
  }, [gameState.confirmMode, showConfirmationDialog, actualValidateConnection]);

  const startSolutionAnimation = useCallback((onComplete?: () => void) => {
    console.log('🎯 Starting solution animation');
    const wordsCount = gameState.wordsInOrder.length;
    const userCorrectConnections = gameState.connections.filter(conn => conn.isCorrect);
    const isFailedGame = gameState.errors >= gameState.maxErrors;
    
    // Generate all solution connections (grey for missing, green for user's correct ones)
    const solutionConnections: Connection[] = [];
    for (let i = 0; i < gameState.wordsInOrder.length; i++) {
      const word1 = gameState.wordsInOrder[i];
      const word2 = gameState.wordsInOrder[(i + 1) % gameState.wordsInOrder.length];
      
      // Check if user made this connection
      const userMadeThis = userCorrectConnections.some(conn => 
        (conn.from === word1 && conn.to === word2) || 
        (conn.from === word2 && conn.to === word1)
      );
      
      solutionConnections.push({
        from: word1,
        to: word2,
        color: userMadeThis ? 'green' : 'gray',
        isCorrect: true,
        isRevealed: !userMadeThis // Mark as revealed if user didn't make this connection
      });
    }
    
    setGameState(prev => ({
      ...prev,
      showingSolution: true,
      solutionAnimationStep: 0,
      words: isFailedGame ? [...prev.words] : [...prev.wordsInOrder], // Keep current positions for failed games
      connections: isFailedGame ? [...prev.connections] : solutionConnections, // Keep user connections for failed games initially
      selectedWord: null,
      secondSelectedWord: null,
      isDragging: false,
      hoveredWord: null,
    }));
    
    if (isFailedGame) {
      // For failed games: move words one at a time to solution positions
      let step = 0;
      const animationInterval = setInterval(() => {
        step++;
        console.log(`🔄 Solution animation step ${step} for failed game`);
        
        setGameState(prev => {
          const targetIndex = step - 1;
          
          if (targetIndex < prev.wordsInOrder.length) {
            // Rearrange words array step by step to move them to solution order
            const newWords = [...prev.words];
            const targetWord = prev.wordsInOrder[targetIndex];
            const currentIndex = newWords.indexOf(targetWord);
            
            console.log(`Moving word "${targetWord}" from position ${currentIndex} to position ${targetIndex}`);
            
            // Only move if the word is not already in the correct position
            if (currentIndex !== targetIndex) {
              // Remove word from current position
              newWords.splice(currentIndex, 1);
              // Insert at target position
              newWords.splice(targetIndex, 0, targetWord);
              
              console.log('New word order:', newWords);
              
              // The GameBoard will recalculate positions based on the new word order
              // This will trigger the animation in GameBoard component
              return {
                ...prev,
                solutionAnimationStep: step,
                words: newWords
              };
            }
          }
          
          // After all words are positioned, show all solution connections
          if (step >= wordsCount) {
            console.log('Showing solution connections');
            return {
              ...prev,
              solutionAnimationStep: step,
              connections: solutionConnections
            };
          }
          
          return {
            ...prev,
            solutionAnimationStep: step
          };
        });
        
        // Stop animation after showing complete solution
        if (step >= wordsCount + 1) {
          clearInterval(animationInterval);
          console.log('✅ Failed game solution animation complete, pausing before modal...');
          
          // Pause for 2-3 seconds to show solution, then navigate to game over modal
          setTimeout(() => {
            console.log('🎊 Navigating to completion modal');
            if (onComplete) {
              onComplete();
            }
          }, 1000);
        }
      }, 1200); // Increased from 800ms to 1200ms to make it more visible
    } else {
      // For successful games: original animation behavior
      let step = 0;
      const animationInterval = setInterval(() => {
        step++;
        setGameState(prev => ({
          ...prev,
          solutionAnimationStep: step,
        }));
        
        // Stop animation after showing complete solution
        if (step >= wordsCount - 1) {
          clearInterval(animationInterval);
          console.log('✅ Solution animation complete, pausing before modal...');
          
          // Pause for 2.5 seconds to show solution, then navigate to completion modal
          setTimeout(() => {
            console.log('🎊 Navigating to completion modal');
            if (onComplete) {
              onComplete();
            }
          }, 1000);
        }
      }, 800);
    }
  }, [gameState.wordsInOrder, gameState.connections, gameState.errors, gameState.maxErrors]);
  const startSolutionAnimationRef = useRef(startSolutionAnimation);
  startSolutionAnimationRef.current = startSolutionAnimation;

  const updateWordPositions = useCallback((positions: Record<string, WordPosition>) => setGameState(prev => ({ ...prev, wordPositions: positions })), []);
  const shuffleWords = useCallback(() => setGameState(prev => ({ ...prev, words: shuffleArray([...prev.words]) })), []);
  const rearrangeWords = useCallback(() => setGameState(prev => ({ ...prev, words: shuffleArray([...prev.words]) })), []);

  const clearGameState = useCallback(() => {
    const key = getTodayKey();
    localStorage.removeItem(`pathways-game-${key}`);
    localStorage.removeItem(`pathways-completion-shown-${key}`);
    setGameState(prev => ({
      ...initialGameState,
      wordsInOrder: prev.wordsInOrder,
      words: shuffleArray(prev.wordsInOrder),
      correctPairs: prev.correctPairs,
      correctConnectionsCount: prev.wordsInOrder.reduce((acc, word) => ({ ...acc, [word]: 0 }), {}),
    }));
  }, []);

  const resetGame = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      words: shuffleArray(prev.wordsInOrder),
      wordPositions: {},
      connections: [],
      errors: 0,
      selectedWord: null,
      secondSelectedWord: null,
      isDragging: false,
      hoveredWord: null,
      gameCompleted: false,
      shareMessage: 'Pathways (pathways1.web.app)\n',
    }));
  }, []);

  const setAnimationCallbacks = useCallback((jumpWords: (word1: string, word2: string) => void, wiggleWords: (word1: string, word2: string) => void) => {
    setGameState(prev => ({
      ...prev,
      jumpWords,
      wiggleWords,
    }));
  }, []);

  const navigationCallbackRef = useRef<(() => void) | null>(null);

  const setNavigationCallback = useCallback((callback: () => void) => {
    navigationCallbackRef.current = callback;
  }, []);

  const memoizedActions = useMemo(() => ({
    initializeGame,
    loadGameState,
    selectWord,
    setDragging,
    setHoveredWord,
    setDragMode,
    validateConnection,
    resetGame,
    updateWordPositions,
    shuffleWords,
    rearrangeWords,
    clearGameState,
    setFontSize: (scale: number) => {
      setGameState(prev => ({ ...prev, fontSizeScale: scale }));
      // Always save to Firestore when user is available
      if (user) {
        saveUserSettings(user, { fontSizeScale: scale });
      }
    },
    setConfirmMode: (confirm: boolean) => {
      setGameState(prev => ({ ...prev, confirmMode: confirm }));
      // Always save to Firestore when user is available
      if (user) {
        saveUserSettings(user, { confirmMode: confirm });
      }
    },
    showToast,
    hideToast,
    showConfirmationDialog,
    hideConfirmationDialog,
    confirmConnection,
    setAnimationCallbacks,
    setNavigationCallback,
    getAnimation,
    isWordSelectable: (word: string) => (gameState.correctConnectionsCount[word] || 0) < 2,
    checkGameCompletion: () => false,
  }), [
    initializeGame, loadGameState, selectWord, setDragging, setHoveredWord, setDragMode, validateConnection, resetGame, updateWordPositions, shuffleWords, rearrangeWords, clearGameState, showToast, hideToast, showConfirmationDialog, hideConfirmationDialog, confirmConnection, setAnimationCallbacks, setNavigationCallback, getAnimation, gameState.correctConnectionsCount, user
  ]);

  return {
    gameState,
    actions: memoizedActions,
  };
};
